// GtAiColumnRun — ask a bench's AI column for chosen people (or everyone not
// yet answered), with the cost first and progress to the end. Opened on one
// person it also shows their current answer and the evidence behind it.
import { useState, useEffect, useRef } from 'react';
import { GtSelect } from './GtControls';

const json = { 'Content-Type': 'application/json' };
function money(n) { return '£' + Number(n || 0).toFixed(2); }
function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
function ukDate(d) {
  const x = new Date(d);
  if (isNaN(x.getTime())) return '';
  return String(x.getDate()).padStart(2, '0') + '/' + String(x.getMonth() + 1).padStart(2, '0') + '/' + x.getFullYear();
}
function answerLabel(a) { return a === 'yes' ? 'Yes' : a === 'no' ? 'No' : a === 'unknown' ? 'Unknown' : a; }

// One answer with its quote, the facts it rests on and where each came from.
// Shared by the bench and the candidate record.
export function AiAnswerDetail({ value, currentQuestion }) {
  const v = value;
  if (!v || typeof v !== 'object') return null;
  return (
    <div className={'gtai-answer gtai-answer--' + (['yes', 'no', 'unknown'].indexOf(v.a) !== -1 ? v.a : 'text')}>
      <div className="gtai-answer-top"><b>{answerLabel(v.a)}</b>{v.c != null && v.a !== 'unknown' && <span>{Math.round(v.c * 100)}% sure</span>}</div>
      {v.e && <p className="gtai-quote">{v.a === 'unknown' ? v.e : '“' + v.e + '”'}</p>}
      {Array.isArray(v.src) && v.src.length > 0 && (
        <div className="gtai-src">
          <span className="gtai-src-h">Based on {v.src.length === 1 ? 'this fact' : 'these facts'} in their profile{v.checked ? ', quote checked' : ''}</span>
          <ul>
            {v.src.map((x, i) => (
              <li key={i}>
                <em className="gtai-origin">{x.o}</em>
                <span className="gtai-fact">{x.t}</span>
                {x.u && <a href={x.u} target="_blank" rel="noreferrer">Source</a>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {v.at && <p className="gtai-meta">Answered {ukDate(v.at)} by artificial intelligence{v.q && currentQuestion && v.q !== currentQuestion ? ' to an earlier question: "' + v.q + '"' : ''}</p>}
    </div>
  );
}

export default function GtAiColumnRun({ bench, fields, initialKey, memberIds, members, authFetch, flash, onClose, onDone }) {
  const aiFields = (fields || []).filter(f => f.type === 'ai');
  const [key, setKey] = useState(initialKey || (aiFields[0] ? aiFields[0].key : ''));
  const [est, setEst] = useState(null);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const field = aiFields.find(f => f.key === key) || null;
  const one = memberIds && memberIds.length === 1 ? (members || []).find(m => m.id === memberIds[0]) : null;
  const current = one && field ? (one.custom_values || {})[field.key] : null;
  const idsKey = memberIds ? memberIds.join(',') : '';

  useEffect(() => {
    if (!key) return;
    setEst(null); setError('');
    const body = { key, ...(idsKey ? { member_ids: idsKey.split(',').map(Number) } : {}) };
    authFetch('/api/benches/' + bench.id + '/ai/estimate', { method: 'POST', headers: json, body: JSON.stringify(body) })
      .then(setEst).catch(e => setError(e.message || 'Could not work out the cost.'));
  }, [authFetch, bench.id, key, idsKey]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  async function start() {
    setBusy(true); setError('');
    try {
      const body = { key, ...(memberIds ? { member_ids: memberIds } : {}) };
      const r = await authFetch('/api/benches/' + bench.id + '/ai/run', { method: 'POST', headers: json, body: JSON.stringify(body) });
      const poll = async () => {
        try {
          const j = (await authFetch('/api/benches/' + bench.id + '/ai/jobs/' + r.job.id)).job;
          setJob(j);
          if (j.status !== 'done') { timer.current = setTimeout(poll, 1200); return; }
          setBusy(false);
          if (onDone) onDone();
          if (flash && !one) flash(plural(j.done, 'answer', 'answers') + ' filled in' + (j.failed ? ', ' + j.failed + ' could not be answered' : '') + '.', j.failed > 0);
        } catch (e) { setError(e.message); setBusy(false); }
      };
      setJob({ total: r.job.total, done: 0, failed: 0, status: 'running' });
      poll();
    } catch (e) { setError(e.message || 'Could not start.'); setBusy(false); }
  }

  const finished = job && job.status === 'done';
  const pct = job && job.total ? Math.round(((job.done + job.failed) / job.total) * 100) : 0;

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtai-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">{one ? (field ? field.label : 'AI column') + ' for ' + (one.full_name || one.ref) : 'Ask an AI column'}</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          {!aiFields.length ? <p className="gtbn-help">This bench has no AI columns yet. Add one under Columns.</p> : (
            <>
              {!initialKey && aiFields.length > 1 && (
                <div className="gtc-field"><span>Column</span>
                  <GtSelect value={key} onChange={setKey} options={aiFields.map(f => ({ value: f.key, label: f.label }))} />
                </div>
              )}
              {field && (
                <div className="gtai-q">
                  <span>Question</span>
                  <b>{(field.options && field.options.question) || 'No question set'}</b>
                </div>
              )}
              {one && current && typeof current === 'object' && <AiAnswerDetail value={current} currentQuestion={field && field.options && field.options.question} />}
              {!job && est && (
                <>
                  <p className="gtbn-help">
                    Answered from what we hold about each person: profile, experience, skills, publications and assessment.
                    Name, contact details and location are not used. "Unknown" means their profile does not say.
                  </p>
                  {!one && (
                    <div className="gten-cost">
                      <div><span>People to ask</span><b>{est.count}</b></div>
                      <div><span>Estimated cost</span><b>{money(est.total_gbp)}</b></div>
                      <div><span>Already answered</span><b>{memberIds ? '—' : Math.max(0, est.on_bench - est.count)}</b></div>
                    </div>
                  )}
                  {!one && !memberIds && est.count === 0 && <p className="gtbn-help">Everyone on this bench has an answer. Tick people in the table to ask them again.</p>}
                </>
              )}
              {job && (
                <div className={'gten-progress' + (finished ? ' gten-progress--done' : '')} role="status">
                  <div className="gten-progress-text">
                    <b>{finished ? 'Done' : 'Asking ' + plural(job.total, 'person', 'people')}</b>
                    <span>{job.done + job.failed} of {job.total}{job.failed ? ' · ' + job.failed + ' could not be answered' : ''}{finished && job.cost_gbp != null ? ' · cost ' + money(job.cost_gbp) : ''}</span>
                  </div>
                  <div className="gten-progress-bar"><span style={{ width: Math.max(4, pct) + '%' }} /></div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          {finished
            ? <button className="gtc-primary" onClick={onClose}>Done</button>
            : (
              <>
                <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>{one ? 'Close' : 'Cancel'}</button>
                <button className="gtc-primary" disabled={busy || !est || !est.count} onClick={start}>
                  {busy ? 'Asking…' : one ? (current ? 'Ask again' : 'Ask') : est && est.count ? 'Ask ' + plural(est.count, 'person', 'people') : 'Nobody to ask'}
                </button>
              </>
            )}
        </div>
      </div>
    </div>
  );
}
